import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from '@/ui';
import { WorkspaceDetailsStep } from './steps/WorkspaceDetailsStep';
import {
  WorkspaceMembersStep,
  WorkspaceMember,
} from './steps/WorkspaceMembersStep';
import { WorkspaceReviewStep } from './steps/WorkspaceReviewStep';
import { WorkspaceTypeStep } from './steps/WorkspaceTypeStep';
import {
  postCreateWorkspace,
  PostCreateWorkspaceRequestBody,
} from '../../../apis/core/workspace/post/postCreateWorkspace';
import {
  FormattedClientData,
  FormattedMatterData,
} from '../../../hooks/data/clientMatter/useClientMatterSearch';
import { useUserDetails } from '../../../hooks/useUserDetails';
import { UseAuth } from '../../../contexts/AuthContext';
import { useWorkspaceErrorHandler } from '../../../hooks/useWorkspaceErrorHandler';
import {
  LAST_CREATED_WORKSPACE_ID_STORAGE_KEY,
  LAST_CREATED_WORKSPACE_TITLE_STORAGE_KEY,
} from '@/components/pages/landingPage/Landingpage.constants';

export interface CreateWorkspaceProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface WorkspaceUser {
  userId: string;
  isOwner: boolean;
}

export interface WorkspaceData {
  type?: 'client-matter' | 'non-client-matter';
  name: string;
  clientId?: string;
  matterId?: string;
  // Stored for display / review
  client?: FormattedClientData;
  matter?: FormattedMatterData;
  description: string;
  template?: 'm&a' | 'litigation' | 'blank' | 'custom';
  members: WorkspaceMember[];
  privacy?: 'private' | 'team' | 'public';
}

const ModalContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  height: 100%;
`;

const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 0;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ProgressStep = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 0.875rem;
  font-weight: 600;
  border: 2px solid
    ${({ active, completed, theme }) =>
      active || completed ? theme.colors?.primary || '#0078d4' : '#e1e1e1'};
  background-color: ${({ active, completed, theme }) =>
    active
      ? theme.colors?.primary || '#0078d4'
      : completed
        ? '#e3f2fd'
        : 'transparent'};
  color: ${({ active, completed, theme }) =>
    active ? 'white' : completed ? theme.colors?.primary || '#0078d4' : '#999'};
  transition: all 0.2s ease;
`;

const ProgressConnector = styled.div<{ active: boolean }>`
  width: 40px;
  height: 2px;
  background-color: ${({ active, theme }) =>
    active ? theme.colors?.primary || '#0078d4' : '#e1e1e1'};
  transition: all 0.2s ease;
`;

const Content = styled.div`
  flex: 1;
  width: 60%;
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  background-color: #ffeaea;
  color: #b71c1c;
  border: 1px solid #f44336;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #e1e1e1;
  gap: 0.5rem;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const STEPS = [
  { id: 1, title: 'Workspace Type' },
  { id: 2, title: 'Details' },
  { id: 3, title: 'Members' },
  { id: 4, title: 'Review' },
];

const hasWorkspaceDetails = (workspaceData: WorkspaceData) => {
  const hasName = workspaceData.name.trim() !== '';
  const hasDescription = workspaceData.description.trim() !== '';
  const hasClient = Boolean(workspaceData.clientId?.trim());
  const hasMatter = Boolean(workspaceData.matterId?.trim());

  if (workspaceData.type === 'client-matter') {
    return hasName && hasDescription && hasClient && hasMatter;
  }

  return hasName && hasDescription;
};

const getErrorResponse = (err: unknown): unknown => {
  if (typeof err !== 'object' || err === null) {
    return undefined;
  }
  const candidate = err as { response?: unknown; body?: unknown };
  return candidate.response ?? candidate.body;
};

export const CreateWorkspace: React.FC<CreateWorkspaceProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const { getAccessToken } = UseAuth();
  const { userDetails, getUserDetails } = useUserDetails(getAccessToken);
  const { handleError, handleSuccess } = useWorkspaceErrorHandler();
  const [currentStep, setCurrentStep] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaceData, setWorkspaceData] = useState<WorkspaceData>({
    type: undefined,
    name: '',
    matterId: undefined,
    client: undefined,
    matter: undefined,
    description: '',
    template: undefined,
    members: [],
    privacy: 'team',
    clientId: undefined,
  });

  useEffect(() => {
    getUserDetails();
  }, [getUserDetails]);

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setCurrentStep(1);
    setError(null);
    setIsCreating(false);
    setWorkspaceData({
      type: undefined,
      name: '',
      matterId: undefined,
      client: undefined,
      matter: undefined,
      description: '',
      template: undefined,
      members: [],
      privacy: 'team',
      clientId: undefined,
    });
    onClose();
  };

  const handleCreateWorkspace = async () => {
    setIsCreating(true);
    setError(null);

    try {
      // Map all members (including creator) to workspaceUsers
      // The creator is already in the members array with isCreator flag
      const workspaceUsers = workspaceData.members.map((member) => ({
        userId: member.id,
        isOwner: member.isOwner,
      }));
      // Map workspaceData to API format
      const requestBody: PostCreateWorkspaceRequestBody = {
        title: workspaceData.name,
        description: workspaceData.description || '',
        workspaceUsers,
        // Map matterId to matterUno if available
        ...(workspaceData.matterId && {
          matterUno: workspaceData.matterId,
        }),
      };

      console.log('Creating workspace with data:', requestBody);

      const response = await postCreateWorkspace({
        getAccessToken: getAccessToken,
        body: requestBody,
      });

      // Check if response has an id (workspace ID)
      if (response && response.id) {
        console.log('Workspace created successfully:', response);

        // Persist the last created workspace so tutorials can reference it.
        try {
          localStorage.setItem(
            LAST_CREATED_WORKSPACE_ID_STORAGE_KEY,
            String(response.id),
          );
          localStorage.setItem(
            LAST_CREATED_WORKSPACE_TITLE_STORAGE_KEY,
            workspaceData.name,
          );
          // Also persist for current browser session so follow-up guides can use it
          // without requiring persistent UI shortcuts.
          sessionStorage.setItem(
            LAST_CREATED_WORKSPACE_ID_STORAGE_KEY,
            String(response.id),
          );
          sessionStorage.setItem(
            LAST_CREATED_WORKSPACE_TITLE_STORAGE_KEY,
            workspaceData.name,
          );
        } catch {
          // Ignore storage failures (private mode, quota, etc.).
        }

        handleSuccess('Workspace created successfully');
        // Close modal and redirect to workspace
        handleClose();
        navigate(`/workspace/${response.id}`);
      } else {
        throw new Error('Workspace created but no ID returned');
      }
    } catch (err: unknown) {
      const normalizedError =
        err instanceof Error ? err : new Error('Failed to create workspace');
      const response = getErrorResponse(err);

      console.error('Failed to create workspace:', normalizedError);
      handleError(normalizedError, 'Failed to create workspace', response);

      setError('Failed to create workspace. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const updateWorkspaceData = (updates: Partial<WorkspaceData>) => {
    setWorkspaceData((prev) => ({ ...prev, ...updates }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <WorkspaceTypeStep
            workspaceData={workspaceData}
            onUpdate={updateWorkspaceData}
          />
        );
      case 2:
        return (
          <WorkspaceDetailsStep
            workspaceData={workspaceData}
            onUpdate={updateWorkspaceData}
          />
        );
      case 3:
        return (
          <WorkspaceMembersStep
            userDetails={userDetails}
            workspaceData={workspaceData}
            onUpdate={updateWorkspaceData}
          />
        );
      case 4:
        return <WorkspaceReviewStep workspaceData={workspaceData} />;
      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return workspaceData.type !== undefined;
      case 2:
        return hasWorkspaceDetails(workspaceData);
      case 3:
        return true; // Members step is optional
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create a New Workspace"
      size="full"
      height="100%"
      footerAlign="center"
      footer={
        <Footer>
          <Button
            variant="outline"
            onClick={handleBack}
            data-tour="create-ws-back-button"
            disabled={currentStep === 1}
          >
            Back
          </Button>
          <FooterActions>
            {currentStep < STEPS.length ? (
              <Button
                variant="primary"
                onClick={handleNext}
                data-tour="create-ws-continue-button"
                disabled={!canProceed()}
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleCreateWorkspace}
                data-tour="create-ws-submit-button"
                disabled={isCreating}
              >
                {isCreating ? 'Creating...' : 'Create Workspace'}
              </Button>
            )}
          </FooterActions>
        </Footer>
      }
      header={
        <ProgressBar>
          {STEPS.map((step, index) => (
            <Fragment key={step.id}>
              <ProgressStep
                active={currentStep === step.id}
                completed={currentStep > step.id}
              >
                {currentStep > step.id ? '✓' : step.id}
              </ProgressStep>
              {index < STEPS.length - 1 && (
                <ProgressConnector active={currentStep > step.id} />
              )}
            </Fragment>
          ))}
        </ProgressBar>
      }
    >
      <ModalContent>
        {/* Content */}
        <Content>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {renderStep()}
        </Content>
      </ModalContent>
    </Modal>
  );
};
